import React, { useEffect, useRef, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';

const CONSENT_KEY = 'portfolio-anonymous-analytics';
const SECTIONS = new Set(['home', 'about', 'experience', 'professional-work', 'projects', 'open-source', 'skills', 'education', 'contact']);

function browserPrivacySignal() {
  return navigator.globalPrivacyControl === true || navigator.doNotTrack === '1' || window.doNotTrack === '1';
}

function readChoice() {
  if (browserPrivacySignal()) return 'declined';
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'allowed' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}

function currentSection() {
  const section = window.location.hash.replace(/^#/, '');
  return SECTIONS.has(section) ? section : 'home';
}

function sanitizedPageview(event) {
  try {
    const url = new URL(event.url, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname === '/admin') return null;
    url.search = '';
    url.hash = '';
    return { ...event, url: url.toString() };
  } catch {
    return null;
  }
}

export function trackContactSubmission() {
  sendAnonymousEvent('contact_submission');
}

function sendAnonymousEvent(type, section) {
  if (readChoice() !== 'allowed') return;
  fetch('/api/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    keepalive: true,
    body: JSON.stringify(section ? { type, section } : { type }),
  }).catch(() => { /* Analytics must never interrupt navigation or contact. */ });
}

const AnalyticsController = () => {
  const [choice, setChoice] = useState(readChoice);
  const [showChoice, setShowChoice] = useState(() => readChoice() === null);
  const [section, setSection] = useState(currentSection);
  const landingSection = useRef(currentSection());

  useEffect(() => {
    const onHashChange = () => setSection(currentSection());
    const openPreferences = () => setShowChoice(true);
    window.addEventListener('hashchange', onHashChange);
    window.addEventListener('open-analytics-preferences', openPreferences);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
      window.removeEventListener('open-analytics-preferences', openPreferences);
    };
  }, []);

  useEffect(() => {
    if (choice !== 'allowed') return undefined;
    const onClick = (event) => {
      const anchor = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!anchor) return;
      let name = anchor.dataset.analyticsEvent;
      if (!name) {
        try {
          const destination = new URL(anchor.href);
          if (destination.hostname === 'github.com') name = 'github_click';
          if (destination.hostname === 'www.linkedin.com' || destination.hostname === 'linkedin.com') name = 'linkedin_click';
        } catch {
          return;
        }
      }
      if (name) sendAnonymousEvent(name);
    };
    document.addEventListener('click', onClick, true);
    sendAnonymousEvent('landing', landingSection.current);
    return () => document.removeEventListener('click', onClick, true);
  }, [choice]);

  const saveChoice = (value) => {
    if (browserPrivacySignal()) return;
    try { localStorage.setItem(CONSENT_KEY, value); } catch { /* Continue for this page only. */ }
    setChoice(value);
    setShowChoice(false);
  };

  return (
    <>
      {choice === 'allowed' && !browserPrivacySignal() && <Analytics route={`/${section}`} path={section === 'home' ? '/' : `/${section}`} beforeSend={sanitizedPageview} />}
      {showChoice && !browserPrivacySignal() && <aside className="analytics-choice" aria-label="Anonymous analytics preference">
        <div><strong>Anonymous site analytics</strong><p>May I count visits and portfolio interactions to improve this site? Reports use Vercel and short-lived aggregate event counters, without advertising cookies or visitor profiles. <a href="/privacy.html">Privacy notice</a></p></div>
        <div className="analytics-choice-actions"><button type="button" onClick={() => saveChoice('declined')}>No thanks</button><button type="button" onClick={() => saveChoice('allowed')}>Allow analytics</button></div>
      </aside>}
    </>
  );
};

export default AnalyticsController;
